import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ScoreRing, PriorityBadge } from "@/components/Score";
import { PitchSequencer } from "@/components/PitchSequencer";
import { ModeBadge } from "@/components/Brand";
import { shortMoney, moneyRange } from "@/lib/format";
import { Loader2, Star, Trash2, KanbanSquare, IndianRupee, Trophy, GripVertical, ExternalLink, StickyNote } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["New", "Researching", "Contacted", "Replied", "Meeting", "Proposal", "Won", "Lost"];
const mid = (o) => Math.round((o.estimated_value_min + o.estimated_value_max) / 2);

function LeadCard({ l, currency, onOpen, onStar, onDragStart, dragging }) {
  const o = l.opportunity;
  return (
    <div draggable onDragStart={(e) => onDragStart(e, l)} onClick={() => onOpen(l)}
      className={`lf-card p-3 cursor-grab active:cursor-grabbing select-none transition-opacity ${dragging ? "opacity-40" : ""}`} data-testid={`lead-${l.id}`}>
      <div className="flex items-start gap-2">
        <ScoreRing score={o.opportunity_score} size={36} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-100 truncate" title={o.name}>{o.name}</p>
          <p className="text-[11px] text-slate-500 truncate">{o.primary_service}</p>
        </div>
        <button onClick={(e) => { e.stopPropagation(); onStar(l); }} data-testid={`star-${l.id}`}><Star className={`w-4 h-4 ${l.starred ? "text-amber-400 fill-amber-400" : "text-slate-600"}`} /></button>
      </div>
      <div className="flex items-center justify-between mt-2">
        <p className="text-[11px] font-semibold text-emerald-400">{l.status === "Won" && l.won_value ? shortMoney(l.won_value, o.currency || currency) : moneyRange(o.estimated_value_min, o.estimated_value_max, o.currency || currency)}</p>
        <span className="flex items-center gap-1.5">{l.notes && <StickyNote className="w-3 h-3 text-slate-500" />}<GripVertical className="w-3.5 h-3.5 text-slate-700" /></span>
      </div>
    </div>
  );
}

export default function Pipeline() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const currency = profile?.currency || "INR";
  const [leads, setLeads] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [over, setOver] = useState(null);
  const [active, setActive] = useState(null);

  const load = () => api.get("/leads").then(({ data }) => setLeads(data));
  useEffect(() => { load(); }, []);

  const update = async (id, patch) => {
    const { data } = await api.patch(`/leads/${id}`, patch);
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, ...data } : l)));
    setActive((a) => (a && a.id === id ? { ...a, ...data } : a));
    return data;
  };
  const remove = async (id) => { await api.delete(`/leads/${id}`); setLeads((ls) => ls.filter((l) => l.id !== id)); setActive(null); toast.success("Lead removed"); };

  const onDrop = async (st) => {
    setOver(null);
    if (!dragId) return;
    const l = leads.find((x) => x.id === dragId);
    setDragId(null);
    if (!l || l.status === st) return;
    setLeads((ls) => ls.map((x) => (x.id === l.id ? { ...x, status: st } : x)));
    try { await update(l.id, { status: st }); toast.success(`${l.opportunity.name} → ${st}`); }
    catch { toast.error("Could not move lead"); load(); }
  };

  if (!leads) return <div className="h-[60vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const potential = leads.filter((l) => l.status !== "Won" && l.status !== "Lost").reduce((s, l) => s + mid(l.opportunity), 0);
  const won = leads.filter((l) => l.status === "Won").reduce((s, l) => s + (l.won_value || mid(l.opportunity)), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-50 flex items-center gap-2"><KanbanSquare className="w-5 h-5 text-primary" /> Pipeline</h1>
          <p className="text-sm text-slate-400 mt-1">{leads.length} saved leads · drag cards between stages</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="lf-card px-4 py-2" data-testid="pipeline-potential">
            <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1"><IndianRupee className="w-3 h-3" /> Potential pipeline</p>
            <p className="font-display text-lg font-bold text-emerald-400 lf-tabnum">{shortMoney(potential, currency)}</p>
          </div>
          <div className="lf-card px-4 py-2" data-testid="pipeline-won">
            <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1"><Trophy className="w-3 h-3" /> Won revenue</p>
            <p className="font-display text-lg font-bold text-amber-400 lf-tabnum">{shortMoney(won, currency)}</p>
          </div>
          <Button className="bg-primary hover:bg-emerald-600 text-white" data-testid="pipeline-find-btn" onClick={() => navigate("/app/opportunities")}>Find more clients</Button>
        </div>
      </div>

      {leads.length === 0 ? (
        <div className="lf-card p-12 text-center">
          <IndianRupee className="w-8 h-8 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-300 font-medium">No leads saved yet.</p>
          <p className="text-sm text-slate-500 mt-1 mb-5">Save opportunities to track them through your pipeline.</p>
          <Button className="bg-primary hover:bg-emerald-600 text-white" onClick={() => navigate("/app/opportunities")}>Find my best clients</Button>
        </div>
      ) : (
        <div className="flex gap-3 items-start overflow-x-auto pb-3 -mx-1 px-1" data-testid="pipeline-board">
          {STATUSES.map((st) => {
            const items = leads.filter((l) => l.status === st);
            const isOver = over === st;
            return (
              <div key={st} onDragOver={(e) => { e.preventDefault(); if (over !== st) setOver(st); }} onDragLeave={() => setOver(null)} onDrop={() => onDrop(st)}
                className={`w-[230px] shrink-0 space-y-2 rounded-xl p-1.5 min-h-[160px] border transition-colors ${isOver ? "border-primary/50 bg-primary/5" : "border-slate-800/60 bg-slate-900/20"}`} data-testid={`column-${st}`}>
                <div className="flex items-center justify-between px-1">
                  <span className={`text-[11px] font-mono uppercase tracking-wider ${st === "Won" ? "text-amber-400" : st === "Lost" ? "text-slate-600" : "text-slate-400"}`}>{st}</span>
                  <span className="text-[11px] text-slate-600" data-testid={`count-${st}`}>{items.length}</span>
                </div>
                {items.map((l) => <LeadCard key={l.id} l={l} currency={currency} dragging={dragId === l.id} onOpen={setActive} onStar={(x) => update(x.id, { starred: !x.starred })} onDragStart={(e, x) => { setDragId(x.id); e.dataTransfer.effectAllowed = "move"; }} />)}
              </div>
            );
          })}
        </div>
      )}

      <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <SheetContent className="w-full sm:max-w-2xl bg-background border-slate-800 overflow-y-auto" data-testid="lead-sheet">
          {active && (
            <>
              <SheetHeader>
                <div className="flex items-center gap-2"><PriorityBadge label={active.opportunity.priority_label} /><ModeBadge mode={active.opportunity.source === "live" ? "live" : "demo"} testid="lead-source-badge" /></div>
                <SheetTitle className="font-display text-xl text-slate-50">{active.opportunity.name}</SheetTitle>
                <SheetDescription className="text-slate-400">{active.opportunity.primary_service} · {moneyRange(active.opportunity.estimated_value_min, active.opportunity.estimated_value_max, active.opportunity.currency || currency)} · {active.opportunity.area}, {active.opportunity.city}</SheetDescription>
              </SheetHeader>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Stage</p>
                  <Select value={active.status} onValueChange={(v) => update(active.id, { status: v })}>
                    <SelectTrigger data-testid="sheet-status" className="h-9 bg-slate-900 border-slate-700 text-slate-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {active.status === "Won" && (
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Won value (₹)</p>
                    <Input type="number" defaultValue={active.won_value || mid(active.opportunity)} onBlur={(e) => update(active.id, { won_value: Number(e.target.value) || 0 })} className="h-9 bg-slate-900 border-slate-700" data-testid="sheet-won-value" />
                  </div>
                )}
              </div>
              <div className="mt-4">
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Notes</p>
                <Textarea defaultValue={active.notes || ""} placeholder="Call notes, decision maker, objections…" rows={3} onBlur={(e) => e.target.value !== active.notes && update(active.id, { notes: e.target.value }).then(() => toast.success("Notes saved"))} className="bg-slate-900 border-slate-700 text-sm" data-testid="sheet-notes" />
              </div>
              <div className="mt-6 lf-card p-4">
                <p className="font-display font-semibold text-slate-100 mb-3">Pitch Sequencer</p>
                <PitchSequencer oppId={active.opportunity_id} compact />
              </div>
              <div className="mt-5 flex items-center justify-between">
                <Button variant="outline" size="sm" className="border-slate-700 text-slate-300 hover:bg-slate-800" onClick={() => navigate(`/app/opportunity/${active.opportunity_id}`)} data-testid="sheet-open-opportunity"><ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open opportunity brief</Button>
                <button onClick={() => remove(active.id)} className="text-xs text-slate-500 hover:text-red-400 flex items-center gap-1" data-testid="sheet-delete"><Trash2 className="w-3.5 h-3.5" /> Remove lead</button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
